import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { TranslationService } from '../../services/translation.service';
import { ConfigurationService } from '../../services/configuration.service';
import { LanguageService } from '../../services/language.service';
import CardApiModel from './models/card.api-model';
import type ICard from '../../models/tap-and-go/card.model';
import Spinner from '../Shared/Spinner';
import TapAndGoChargesTable from './components/TapAndGoChargesTable';
import CardChargesApiModel from './models/card-charges.api-model';
import type ICardCharges from '../../models/tap-and-go/card-charges.model';
import type ICardCharge from '../../models/tap-and-go/card-charge.model';
import './style.scss';
import GeneralError from '../Shared/GeneralError';
import { Status } from '../../hooks/fetch.hook';
import { SecurityHelper } from '../../helpers/security.helper';

async function fetchCard(token: string): Promise<ICard> {
  const response = await fetch(`/api/tapandgo/card`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  const result: CardApiModel = await response.json();
  return CardApiModel.mapToBusinessModel(result);
}

async function fetchCharges(token: string, limit: number, offset: number): Promise<ICardCharges> {
  const response = await fetch(`/api/tapandgo/charges`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token, limit, offset, contentGuid: ConfigurationService.get('tapandgoreceiptspage.contentguid') }),
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  const result: CardChargesApiModel = await response.json();
  return CardChargesApiModel.mapToBusinessModel(result);
}

function TapAndGoReceipts() {
  const tokeniseButtonRef = useRef<HTMLButtonElement>(null);
  const [showIframeError, setShowIframeError] = useState(false);
  const [tokeniseCardButtonVisible, setTokeniseCardButtonVisible] = useState(false);
  const [iframeVisible, setIframeVisible] = useState(false);
  const [card, setCard] = useState<ICard | undefined>(undefined);
  const [currentCharges, setCurrentCharges] = useState<ICardCharge[]>([]);
  const [getCharges, setGetCharges] = useState<boolean>(false);
  const [showLoadMore, setShowLoadMore] = useState<boolean>(false);
  const [currentToken, setCurrentToken] = useState<string | undefined>(undefined);
  const [chargesLoaded, setChargesLoaded] = useState<number>(0);
  const [cardStatus, setCardStatus] = useState({ status: Status.notperformed, updated: DateTime.now() });

  const isLoading = cardStatus.status === Status.loading;
  const showNoMore = !isLoading && !showLoadMore;
  const chargesLimit = 10;
  const tokenisationToken: string | undefined = ConfigurationService.get('tapandgoreceiptspage.cardtokenisationtoken');

  useEffect(() => {
    function initializeLittlePay(accessToken: string): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).littlepay({
        authorization: accessToken,
        element: '#tokenise-card-btn',
        envUrl: ConfigurationService.get('tapandgoreceiptspage.littlepayenvurl'),
        options: {
          panonlymode: false,
          allowexpiredcardsmode: ConfigurationService.get('tapandgoreceiptspage.enableallowexpiredcardsmode') === 'true',
          targetiframename: 'card-collection-iframe',
          name: 'Västtrafik',
          color: '#0079b4',
          backgroundcolor: '#fff',
          language: LanguageService.getCurrentLanguage(),
        },
        onTokenize(response: { token: string }) {
          setIframeVisible(false);
          setCurrentToken(response.token);
        },
        onVerificationFailure() {
          setShowIframeError(true);
        },
        onError() {
          setShowIframeError(true);
        },
        onCancel() {
          setIframeVisible(false);
          setTokeniseCardButtonVisible(true);
        },
        onSubmit() {
          setShowIframeError(false);
        },
      });
    }
    if (tokenisationToken) {
      initializeLittlePay(tokenisationToken);
      setTokeniseCardButtonVisible(true);
    }
  }, [tokenisationToken]);

  useEffect(() => {
    async function fetchCardData(token: string) {
      try {
        setCardStatus({ status: Status.loading, updated: DateTime.now() });
        const result = await fetchCard(token);
        setCard(result);
        setGetCharges(true);
        setCardStatus({ status: Status.success, updated: DateTime.now() });
      } catch {
        setCardStatus({ status: Status.failed, updated: DateTime.now() });
      }
    }
    if (currentToken) {
      fetchCardData(currentToken);
    }
  }, [currentToken]);

  useEffect(() => {
    async function fetchCardCharges(token: string) {
      try {
        setCardStatus({ status: Status.loading, updated: DateTime.now() });
        const result = await fetchCharges(token, chargesLimit, chargesLoaded);
        setCurrentCharges([...currentCharges, ...result.charges]);
        setCardStatus({ status: Status.success, updated: DateTime.now() });
        setChargesLoaded(chargesLoaded + result.unfilteredCount);
        if (result.charges.length === 0) {
          setShowLoadMore(false);
        } else {
          setShowLoadMore(chargesLoaded + result.unfilteredCount < result.totalCount);
        }
        setGetCharges(false);
      } catch {
        setCardStatus({ status: Status.failed, updated: DateTime.now() });
        setGetCharges(false);
      }
    }
    if (currentToken && getCharges) {
      fetchCardCharges(currentToken);
    }
  }, [currentToken, chargesLoaded, currentCharges, getCharges]);

  useEffect(() => {
    SecurityHelper.initFrameBuster();
  }, []);

  function handleOnTokeniseCardButtonClick() {
    setIframeVisible(true);
    setTokeniseCardButtonVisible(false);
    setShowIframeError(false);
  }

  tokeniseButtonRef.current?.addEventListener('click', handleOnTokeniseCardButtonClick);

  function renderIframe() {
    return (
      <iframe
        className="tap-and-go-receipts__card-collection"
        name="card-collection-iframe"
        title={TranslationService.get('tapandgoreceiptspage.entercarddetails')}
        scrolling="no"
        referrerPolicy="no-referrer"
      ></iframe>
    );
  }

  return (
    <div>
      <div role="alert" aria-live="polite">
        {showIframeError && (
          <div className="mb-3 mb-md-4 tap-and-go-receipts__alert tap-and-go-receipts__alert--error alert ">{TranslationService.get('tapandgoreceiptspage.littlepaygeneralerror')}</div>
        )}
      </div>
      <button
        id="tokenise-card-btn"
        type="button"
        ref={tokeniseButtonRef}
        className={`${tokeniseCardButtonVisible ? '' : 'd-none'} btn btn-primary`}
        aria-hidden={!tokeniseCardButtonVisible}
        tabIndex={tokeniseCardButtonVisible ? undefined : -1}
      >
        {TranslationService.get('tapandgoreceiptspage.entercarddetails')}
      </button>
      <div className={`${iframeVisible ? '' : 'd-none'}`}>{useMemo(() => renderIframe(), [])}</div>
      {card && (
        <div>
          <h2>{TranslationService.get('tapandgoreceiptspage.receiptsheading')}</h2>
          <div className="mb-4">
            <span className="fw-bold">{TranslationService.get('tapandgoreceiptspage.cardnumber')}: </span>
            <span>{card.cardNumber}</span>
          </div>
          {currentCharges.length > 0 && (
            <div>
              <TapAndGoChargesTable id="tap-and-go-charges" charges={currentCharges} card={card} />
              {showLoadMore && !isLoading && (
                <button className="btn--as-link d-block text-center mt-2 mb-0 mx-auto" aria-controls="tap-and-go-charges" onClick={() => setGetCharges(true)}>
                  {TranslationService.get('tapandgoreceiptspage.showmore')}
                </button>
              )}
              {showNoMore && <p className="text-center">{TranslationService.get('tapandgoreceiptspage.nomorereceiptstext')}</p>}
            </div>
          )}
          {!isLoading && !showLoadMore && currentCharges.length === 0 && <div>{TranslationService.get('tapandgoreceiptspage.noreceiptstext')}</div>}
        </div>
      )}
      {isLoading && <Spinner delayInMs={0} />}
      <GeneralError className="mt-2" showError={cardStatus.status === Status.failed || !tokenisationToken} />
    </div>
  );
}

export default TapAndGoReceipts;
